import AppOptions from "../app/options";
import GQL from "../gql/gql";
import Cookies from "./cookies";
import Observer from "./observer";
import Html from "./html";
import { BannerPosition, preOrPostRolls } from "../enum/banner";
import BannerZoneModel from "../model/banner_zone";

declare var window: any;

class Banners {

    banners: Array<BannerZoneModel> = [];
    adsStoreUrl = '';

    loadBannersPromise: Promise<any>;

    commentsObserver: any;

    commentsActiveSlots: { [key: string]: { slot: any; slotId: string } | null } = {
        '/23086084073/D_in_comments': null,
        '/23086084073/D_in_comments_1': null,
        '/23086084073/M_in_comments': null,
        '/23086084073/M_in_comments_1': null
    };
      
    commentsSlotPrefixMap = [
         { 
              prefix: 'div-gpt-ad-1750238051488-', 
              slotName: '/23086084073/D_in_comments', 
              dims: [[1,1],[728,90]], 
              isMobile: false 
         },
         { 
              prefix: 'div-gpt-ad-1750928140596-', 
              slotName: '/23086084073/D_in_comments_1', 
              dims: [[1,1],[728,90]], 
              isMobile: false 
         },
         { 
              prefix: 'div-gpt-ad-1750238123145-', 
              slotName: '/23086084073/M_in_comments', 
              dims: [[1,1],[300,250]], 
              isMobile: true 
         },
         { 
              prefix: 'div-gpt-ad-1750928212141-', 
              slotName: '/23086084073/M_in_comments_1', 
              dims: [[1,1],[300,250]], 
              isMobile: true 
         }
    ];

    constructor(
        protected cookies: Cookies,
        protected html: Html,
        protected observer: Observer,
        protected options: AppOptions,
        protected gql: GQL
    ) {}

    init(): void {
        document.addEventListener('cookies', (event: any) => {
            this.loadBanners(event.detail.isVendorAllowed('c:proplusd-8Yr4QWrg') ? 'all' : 'local');
        });
    }

    loadBanners(cookieAccept: string): void {
        const affiliateId = this.options.isMobile ?
            this.options.affiliates.mobile : this.options.affiliates.desktop;

        this.loadBannersPromise = this.gql.fetchBanners({
                affiliateId: affiliateId,
                categories: this.options.categories,
                sectionId: this.options.sectionId,
                cookieAccept: cookieAccept,
                responseJSON: '',
                hasVast: 1
            })
            .then(r => {
                this.adsStoreUrl = r.adsStoreUrl;
                return Array.isArray(r.banners) ? r.banners : [];
            })
            .then((banners: Array<BannerZoneModel>) => {
                this.banners = banners;
            });
    }

    hideAll(): void {
        this.html.hide('.banner');
    }

    async get(position: number): Promise<BannerZoneModel | null> {
        await this.loadBannersPromise;

        return this.banners.find((bannerZone: BannerZoneModel) => bannerZone.position === position) || null;
    }

    replace(position: number, bannerZone: BannerZoneModel): void {
        this.banners = this.banners.map((bZone: BannerZoneModel) => {
            if (position === bZone.position) {
                bZone.banner = bannerZone.banner;
            }
            return bZone;
        })
    }

    reloadVideoBanners(): void {
        let cookieAccept = this.cookies.cookies?.isVendorAllowed('c:proplusd-8Yr4QWrg') ? 'all' : 'local';

        const affiliateId = this.options.isMobile ?
            this.options.affiliates.mobile : this.options.affiliates.desktop;

        const args = {
            affiliateId: affiliateId,
            categories: this.options.categories,
            sectionId: this.options.sectionId,
            cookieAccept: cookieAccept,
            responseJSON: this.jsonForBannerReload(),
            hasVast: 0,
        };

        this.gql
            .fetchBanners(args)
            .then(r => {
                if (!r.banners || !Array.isArray(r.banners) || !r.banners.length) {
                    return;
                }

                r.banners.forEach((bannerZone: BannerZoneModel) => {
                    if (this.isPreOrPostRoll(bannerZone.position)) {
                        this.replace(bannerZone.position, bannerZone);
                    }
                });
            });
    }

    comments(): void {
        if (this.commentsObserver) {
            this.commentsObserver.disconnect();
        }

        this.commentsObserver = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                if (!entry.isIntersecting) {
                    return;
                }

                const adEl = entry.target.querySelector('[id^="div-gpt-ad-"]');

                if (!adEl) {
                    return;
                }

                const elId = adEl.id;

                const found = this.commentsSlotPrefixMap.find(item => elId.startsWith(item.prefix) && item.isMobile === this.options.isMobile);

                if (!found) {
                    return;
                }

                const slotName = found.slotName;
                const activeSlotData = this.commentsActiveSlots[slotName];

                if (activeSlotData && activeSlotData.slotId === elId) {
                    return;
                }
                
                window.googletag.cmd.push(() => {
                    if (activeSlotData && activeSlotData.slot) {
                        window.googletag.destroySlots([activeSlotData.slot]);
                    }

                    const existing = window.googletag.pubads().getSlots().find((slot: any) => slot.getSlotElementId() === elId);

                    if (existing) {
                        this.commentsActiveSlots[slotName] = { slot: existing, slotId: elId };
                        window.googletag.pubads().refresh([existing]);

                        return;
                    }

                    const slot = window.googletag.defineSlot(slotName, found.dims, elId).addService(window.googletag.pubads());

                    this.commentsActiveSlots[slotName] = { slot, slotId: elId };

                    window.pbjs.que.push(() => {
                        window.pbjs.rp.requestBids({
                            callback: () => {
                                window.googletag.cmd.push(() => {
                                    window.googletag.display(elId);
                                    window.googletag.pubads().refresh([slot]);
                                });
                            },
                            gptSlotObjects: [slot]
                        });
                    });
                });
            });
        }, {
            root: null,
            threshold: 0.1,
            rootMargin: '300px 0px'
        });

        const inCommentBannerEls = this.html.qAll('.banner.comment-ad');
        inCommentBannerEls.forEach(el => this.commentsObserver.observe(el));
    }

    async getPreOrPostRoll(type: string): Promise<BannerZoneModel | null> {
        let position = this.options.isMobile ? BannerPosition.MOBILE_PREROLL : BannerPosition.PREROLL;
        if (type === 'postroll') {
            position = this.options.isMobile ? BannerPosition.MOBILE_POSTROLL : BannerPosition.POSTROLL;
        }

        return await this.get(position);
    }

    private isPreOrPostRoll(position: number): boolean {
        return preOrPostRolls.includes(position);
    }

    private jsonForBannerReload(): string {
        if (!this.banners || !this.banners.length) {
            return '';
        }

        const json = this.banners.map((bannerZone: BannerZoneModel) => {
            if (this.isPreOrPostRoll(bannerZone.position)) {
                return null;
            }

            return {
                p: bannerZone.position,
                b: bannerZone.banner.id,
                z: bannerZone.zoneId
            };
        }).filter((b: any) => b)

        return JSON.stringify(json);
    }

    // Removes 'hidden' class to n random divs inside given element.
    // Needed for displaying random PR articles.
    showRandomPrArticles(selector: string, nb: number): void {
        const children = this.html.q(selector)?.childNodes || [];
        const nbChildren = children.length;

        let selected: Array<number> = [];

        while (selected.length < nb && selected.length < nbChildren) {
            const rnd = Math.floor(Math.random() * nbChildren);
            if (!selected.includes(rnd)) {
                selected.push(rnd);
            }
        }

        (children as HTMLElement[]).forEach((prArticle: HTMLElement, i: number) => {
            if (selected.includes(i)) {
                this.html.removeClass(prArticle, 'hidden');
            };
        });
    }
}

export default Banners;